import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';


import formatEnumLabel from './formatEnumLabel';
import { STATIC_STATUS_COLOR, staticStatusLabel } from './staticStatus';
import { StaticAdSummary, StaticPipelineRow } from './types';

// The dense counterpart to the card grid. The grid is for scanning creative;
// this is for comparing many ads on their extracted attributes at once, which
// is what the video tool's Fetched Ads table is for.
//
// Deliberately a fixed column set rather than a port of VideoCreative's
// ColumnPicker: that carries a 40+ column registry with drag-to-reorder and
// saved presets, and the static extraction has about a dozen meaningful
// attributes. If that changes, the picker is the thing to port.

interface Column {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  render: (ad: StaticAdSummary) => React.ReactNode;
}

/** Every header offered for sorting. Ids must be comparator keys in
 * staticSort (the contract test enforces it) — a header whose id has no
 * comparator renders a sort arrow that silently does nothing. */
export const SORTABLE_COLUMN_IDS = [
  'advertiser', 'staticFormat', 'visualHook', 'layout', 'textDensity',
  'aspectRatio', 'overallGrade', 'pipelineStatus',
] as const;
const isSortable = (id: string) => (SORTABLE_COLUMN_IDS as readonly string[]).includes(id);



interface StaticAdTableProps {
  /** Already sorted by the library, so both views agree on the order. */
  ads: StaticAdSummary[];
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
  statuses: Record<string, StaticPipelineRow>;
  selected: Set<string>;
  onToggleSelect: (staticAdId: string) => void;
  onToggleSelectAll: () => void;
  onOpen: (staticAdId: string) => void;
}

export default function StaticAdTable({
  ads, statuses, selected, onToggleSelect, onToggleSelectAll, onOpen, sortField, sortDir, onSort,
}: StaticAdTableProps) {
  const columns: Column[] = [
    {
      id: 'thumbnail', label: '', sortable: isSortable('thumbnail'),
      render: ad => (ad.thumbnailUrl
        ? <Box component="img" src={ad.thumbnailUrl} alt="" loading="lazy"
            sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 0.5, display: 'block' }} />
        : <Box sx={{ width: 44, height: 44, bgcolor: 'grey.100', borderRadius: 0.5 }} />),
    },
    {
      id: 'advertiser', label: 'Advertiser', sortable: isSortable('advertiser'),
      render: ad => (
        <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 160 }}>
          {ad.advertiserName || ad.pageName || '—'}
        </Typography>
      ),
    },
    {
      id: 'ocrText', label: 'Text on the image', sortable: isSortable('ocrText'),
      render: ad => (
        <Typography variant="caption" color="text.secondary" sx={{
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden', maxWidth: 320,
        }}>
          {ad.headline || ad.ocrText || '—'}
        </Typography>
      ),
    },
    { id: 'staticFormat', label: 'Format', sortable: isSortable('staticFormat'), render: ad => formatEnumLabel(ad.staticFormat) || '—' },
    { id: 'visualHook', label: 'Hook', sortable: isSortable('visualHook'), render: ad => formatEnumLabel(ad.visualHook) || '—' },
    { id: 'layout', label: 'Layout', sortable: isSortable('layout'), render: ad => formatEnumLabel(ad.layout) || '—' },
    { id: 'textDensity', label: 'Text', sortable: isSortable('textDensity'), render: ad => formatEnumLabel(ad.textDensity) || '—' },
    { id: 'aspectRatio', label: 'Ratio', align: 'center', sortable: isSortable('aspectRatio'), render: ad => ad.aspectRatio || '—' },
    {
      id: 'overallGrade', label: 'Grade', align: 'center', sortable: isSortable('overallGrade'),
      render: ad => (ad.overallGrade != null
        ? <Chip size="small" label={`${ad.overallGrade}/5`}
            color={ad.overallGrade >= 4 ? 'success' : 'default'}
            sx={{ height: 20, fontSize: '0.65rem' }} />
        : '—'),
    },
    {
      id: 'pipelineStatus', label: 'Status', align: 'center', sortable: isSortable('pipelineStatus'),
      render: (ad) => {
        // Live status from the poller wins over the value the list was loaded
        // with, so a row finishing mid-session updates in place.
        const status = statuses[ad.staticAdId]?.pipelineStatus ?? ad.pipelineStatus;
        const error = statuses[ad.staticAdId]?.errorMessage ?? ad.errorMessage;
        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Chip size="small" label={staticStatusLabel(status)} color={STATIC_STATUS_COLOR[status] || 'default'}
              sx={{ height: 20, fontSize: '0.65rem' }} />
            {status === 'FAILED' && error && (
              <Tooltip title={error}><ErrorOutlineIcon color="error" sx={{ fontSize: 16 }} /></Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const allSelected = ads.length > 0 && ads.every(a => selected.has(a.staticAdId));
  const someSelected = ads.some(a => selected.has(a.staticAdId)) && !allSelected;

  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={onToggleSelectAll} />
            </TableCell>
            {columns.map(col => (
              <TableCell key={col.id} align={col.align || 'left'}>
                {col.sortable ? (
                  <TableSortLabel
                    active={sortField === col.id}
                    direction={sortField === col.id ? sortDir : 'asc'}
                    onClick={() => onSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ads.map(ad => (
            <TableRow
              key={ad.staticAdId}
              hover
              selected={selected.has(ad.staticAdId)}
              sx={{ cursor: 'pointer' }}
              onClick={() => onOpen(ad.staticAdId)}
            >
              <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                <Checkbox
                  size="small"
                  checked={selected.has(ad.staticAdId)}
                  onChange={() => onToggleSelect(ad.staticAdId)}
                />
              </TableCell>
              {columns.map(col => (
                <TableCell key={col.id} align={col.align || 'left'}>{col.render(ad)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
